import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { api, apiErrorMessage } from "../lib/api";
import { useI18n } from "../lib/i18n";
import { Coins, Lock, Send, Upload, X, ShieldCheck, AlertCircle, Clock } from "lucide-react";

// Iter 116 — Player change-request submission flow.
// Iter 141/142/143 — Verified-state UX (locked banner, OfferedVolumeLocked
//   display, per-vertical delta detection, focus-stable rows, live 50–200%
//   range from /config/wager-coefficients, stacked layout).
// Iter 144 Part 3 — Split into TWO independent panels: CasinoChangePanel
//   + CaseChangePanel. Each sends only its own vertical's fields; only
//   one CR may be open across both verticals at a time (backend pending
//   guard remains per-player). When lock.pendingVertical is set, both
//   panels show a pending banner and both submit buttons are disabled.
//
// NEVER writes offered/categories directly — creates a pending
// playerChangeRequest that an admin reviews before values move.

const CATEGORY_KEYS = ["slots", "house_originals", "table_games", "live_casino", "sports", "dice"];

// Backend bounds mirrored verbatim from server.py L4105-4106.
const OFFERED_MIN_FRACTION = 0.50;
const OFFERED_MAX_FRACTION = 2.00;

// Module-scope so React sees the same component type across renders (fix
// for the Iter 142 focus-remount bug). Chip / OOB banner are siblings
// whose visibility toggles inside a stable subtree — the input never
// remounts regardless of chip/banner state.
function OfferedRow({ vertical, label, currentLocked, val, setVal, weighted, testId, disabled }) {
  const { t } = useI18n();
  const bounds = weighted > 0
    ? { min: Math.round(weighted * OFFERED_MIN_FRACTION), max: Math.round(weighted * OFFERED_MAX_FRACTION) }
    : null;
  const changed = val > 0 && val !== currentLocked;
  const inRange = !changed || (bounds && val >= bounds.min && val <= bounds.max);
  const oob = changed && bounds && !inRange;
  const currentDisplay = currentLocked > 0 ? `$${currentLocked.toLocaleString()}` : t("cr.offeredLocked.notSet");
  const rangeMsg = bounds
    ? t("cr.offered.allowedRange")
        .replace("{min}", `$${bounds.min.toLocaleString()}`)
        .replace("{max}", `$${bounds.max.toLocaleString()}`)
        .replace("{w}", `$${Number(weighted).toLocaleString()}`)
    : t("cr.offered.allowedEmpty");
  return (
    <div className="border border-white/10 p-3" data-testid={`cr-offered-row-${vertical}`}>
      <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
        <span className="text-[11px] font-mono uppercase tracking-widest text-[#F5B841]">{label}</span>
        <span className="text-[10px] font-mono text-[#8B97AE]" data-testid={`cr-offered-current-${vertical}`}>
          {t("cr.offered.currentLocked")} <span className="text-[#E8ECF4] tabular-nums">{currentDisplay}</span>
        </span>
      </div>
      <label className="text-[10px] font-mono uppercase tracking-widest text-[#8B97AE] block mb-1">{t("cr.offered.proposeNew")}</label>
      <input
        data-testid={testId}
        type="text" inputMode="numeric" disabled={disabled}
        value={val ? Number(val).toLocaleString() : ""}
        onChange={(e) => setVal(Number(e.target.value.replace(/[^\d]/g, "")) || 0)}
        className={`w-full rounded-none bg-transparent border h-10 font-mono tabular-nums text-right text-[#E8ECF4] px-3 transition-colors ${oob ? "border-[#F26D6D]" : "border-white/20"} disabled:opacity-40`}
      />
      <div className="text-[10px] font-mono mt-1" data-testid={`cr-offered-range-${vertical}`}>
        <span className={bounds ? "text-[#8B97AE]" : "text-[#F5B841]"}>{rangeMsg}</span>
      </div>
      {oob && (
        <div className="text-[10px] font-mono mt-1 text-[#F26D6D]" data-testid={`cr-offered-oob-${vertical}`}>
          {t("cr.offered.oob")}
        </div>
      )}
      <div className="text-[10px] font-mono mt-1">
        {changed && !oob ? (
          <span className="text-[#2DD4A7]" data-testid={`cr-offered-changed-${vertical}`}>{t("cr.offered.willPropose")}</span>
        ) : !changed ? (
          <span className="text-[#8B97AE]">{t("cr.offered.skip")}</span>
        ) : null}
      </div>
    </div>
  );
}

function PendingBanner({ pendingVertical, submittedAt, onCancel, testIdSuffix }) {
  const { t } = useI18n();
  const label = pendingVertical === "casino" ? t("cr.label.casino") : t("cr.label.case");
  return (
    <div
      className="border border-[#F5B841]/40 bg-[#F5B841]/[0.06] p-3 mb-4 flex items-start gap-2"
      data-testid={`cr-pending-banner-${testIdSuffix}`}
    >
      <Clock className="w-3.5 h-3.5 text-[#F5B841] mt-0.5 shrink-0" />
      <div className="flex-1 text-[11px] font-mono">
        <div className="text-[#F5B841] uppercase tracking-widest mb-1">{t("cr.pending.eyebrow")}</div>
        <div className="text-[#8B97AE]">
          {t("cr.pending.body").split("{v}").map((seg, i) => i === 0 ? seg : <span key={i}><span className="text-[#E8ECF4]">{label}</span>{seg}</span>)}
          {submittedAt ? <span className="text-[#8B97AE]">{t("cr.pending.submittedAt").replace("{t}", submittedAt.slice(0,16).replace("T"," "))}</span> : null}
        </div>
      </div>
      {onCancel && (
        <button
          data-testid={`cr-pending-cancel-${testIdSuffix}`}
          onClick={onCancel}
          className="text-[10px] font-mono uppercase tracking-widest text-[#F26D6D] hover:text-white shrink-0"
        >{t("cr.pending.cancel")}</button>
      )}
    </div>
  );
}

// -------------- CASINO SUB-PANEL --------------
function CasinoChangePanel({ me, disabled, pending, pendingVertical, onCancelPending, onSubmitted }) {
  const { t } = useI18n();
  const currentCasino = Number(me?.offeredVolumeByVertical?.casino || 0);
  const [cats, setCats] = useState(() => Object.fromEntries(CATEGORY_KEYS.map((k) => [k, Number(me?.wagerCategoriesSelfReported?.[k] || 0)])));
  const [offCasino, setOffCasino] = useState(currentCasino);
  const [note, setNote] = useState("");
  const [proofs, setProofs] = useState([]);
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [coeffCfg, setCoeffCfg] = useState(null);
  // Iter 208 — Reporting-period selector (30 | 90 days). Defaults to the
  // player's current casino period so an unchanged pick doesn't have to
  // be re-touched. Captured on submit; admin cannot override on approve.
  const [reportPeriod, setReportPeriod] = useState(() => {
    const p = Number(me?.wagerReportPeriodDays);
    return p === 30 || p === 90 ? p : 90;
  });
  useEffect(() => { api.get("/config/wager-coefficients").then((r) => setCoeffCfg(r.data)).catch(() => {}); }, []);

  const coefs = useMemo(() => {
    const m = {};
    (coeffCfg?.categories || []).forEach((c) => { m[c.key] = Number(c.coefficient || 0); });
    return m;
  }, [coeffCfg]);
  const casinoWeighted = useMemo(() => {
    let s = 0;
    for (const k of CATEGORY_KEYS) s += Number(cats[k] || 0) * Number(coefs[k] || 0);
    return Math.round(s * 100) / 100;
  }, [cats, coefs]);
  const bounds = casinoWeighted > 0
    ? { min: Math.round(casinoWeighted * OFFERED_MIN_FRACTION), max: Math.round(casinoWeighted * OFFERED_MAX_FRACTION) }
    : null;
  const changed = offCasino > 0 && offCasino !== currentCasino;
  const inRange = !changed || (bounds && offCasino >= bounds.min && offCasino <= bounds.max);
  const submitBlocked = disabled || busy || proofs.length === 0 || !changed || !inRange;

  const upload = useCallback(async (files) => {
    setUploading(true);
    try {
      const uploaded = [];
      for (const f of files) {
        const fd = new FormData();
        fd.append("file", f);
        const { data } = await api.post("/players/me/proofs", fd, { headers: { "Content-Type": "multipart/form-data" } });
        uploaded.push({ id: data.id, path: data.path, filename: data.filename, contentType: data.contentType, size: data.size });
      }
      setProofs((old) => [...old, ...uploaded]);
      toast.success(`Uploaded ${files.length} proof${files.length > 1 ? "s" : ""}`);
    } catch (e) { toast.error(apiErrorMessage(e)); }
    finally { setUploading(false); }
  }, []);
  const removeProof = async (idx) => {
    const p = proofs[idx];
    try { await api.delete(`/players/me/proofs?path=${encodeURIComponent(p.path)}`); } catch (e) { /* soft */ }
    setProofs(proofs.filter((_, i) => i !== idx));
  };

  const submit = async () => {
    if (proofs.length === 0) { toast.error(t("cr.toast.attachProof")); return; }
    if (!changed) { toast.error(t("cr.toast.enterDifferentCasino")); return; }
    if (!inRange) { toast.error(t("cr.toast.casinoOob")); return; }
    setBusy(true);
    try {
      const body = {
        vertical: "casino",
        proposedCategories: cats,
        proposedOfferedVolumeByVertical: { casino: offCasino },
        proposedWagerReportPeriodDays: reportPeriod,
        proofFileIds: proofs.map((p) => p.id).filter(Boolean),
        note,
      };
      const { data } = await api.post("/players/me/change-request", body);
      toast.success(t("cr.toast.casinoSubmitted"));
      onSubmitted && onSubmitted(data);
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error((d && typeof d === "object" && d.message) || apiErrorMessage(e));
    } finally { setBusy(false); }
  };

  return (
    <section className="lg:col-span-12 border border-[#F5B841]/25 bg-[#F5B841]/[0.04] p-6 lg:p-8" data-testid="cr-panel-casino">
      <div className="flex items-center gap-2 mb-1">
        <Coins className="w-4 h-4 text-[#F5B841]" />
        <h2 className="font-display font-bold text-lg">{t("cr.casino.title")}</h2>
      </div>
      <p className="text-xs text-[#8B97AE] mb-4 max-w-3xl">
        {t("cr.casino.desc")}
      </p>

      {pending && (
        <PendingBanner
          pendingVertical={pendingVertical}
          submittedAt={pending?.submittedAt}
          onCancel={onCancelPending}
          testIdSuffix="casino"
        />
      )}

      <div className="text-[10px] font-mono uppercase tracking-widest text-[#8B97AE] mb-2" data-testid="cr-cats-label">
        {t("cr.casino.catsLabel")}
        <span className="text-[#E8ECF4] tabular-nums ml-1" data-testid="cr-casino-weighted-live">${casinoWeighted.toLocaleString()}</span>
      </div>
      {/* Iter 208 — Reporting-period selector (30 | 90). Player picks the
          window their new figures cover; captured once at submission,
          becomes the new casino period on approve. Admin cannot override. */}
      <div className="mb-4" data-testid="cr-report-toggles-casino">
        <div className="text-[10px] font-mono uppercase tracking-widest text-[#8B97AE] mb-1">{t("onboarding.reportPeriodLabel")}</div>
        <div className="inline-flex border border-white/20" role="tablist">
          {[30, 90].map((d) => (
            <button
              key={d}
              type="button"
              data-testid={`cr-report-period-casino-${d}`}
              disabled={disabled}
              onClick={() => setReportPeriod(d)}
              className={`px-3 h-9 text-[11px] font-mono uppercase tracking-widest transition-colors disabled:opacity-40 disabled:cursor-not-allowed ${
                Number(reportPeriod) === d
                  ? "bg-[#F5B841] text-[#04150F]"
                  : "text-[#8B97AE] hover:text-[#E8ECF4]"
              }`}
            >{d === 30 ? t("card.wager.period30") : t("card.wager.period90")}</button>
          ))}
        </div>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mb-4">
        {CATEGORY_KEYS.map((k) => (
          <div key={k}>
            <label className="text-[10px] font-mono uppercase tracking-widest text-[#8B97AE] block mb-1">
              {t(`categories.${k}`)}
              {coefs[k] !== undefined && <span className="text-[9px] text-[#8B97AE]/60 ml-1">× {coefs[k]}</span>}
            </label>
            <input
              data-testid={`cr-cat-${k}`}
              type="text" inputMode="numeric" disabled={disabled}
              value={cats[k] ? Number(cats[k]).toLocaleString() : ""}
              onChange={(e) => setCats((old) => ({ ...old, [k]: Number(e.target.value.replace(/[^\d]/g, "")) || 0 }))}
              className="w-full rounded-none bg-transparent border border-white/20 h-10 font-mono tabular-nums text-right text-[#E8ECF4] px-3 disabled:opacity-40"
            />
          </div>
        ))}
      </div>

      <div className="mb-4">
        <OfferedRow
          vertical="casino"
          label={t("cr.label.casino")}
          currentLocked={currentCasino}
          val={offCasino}
          setVal={setOffCasino}
          weighted={casinoWeighted}
          testId="cr-offered-casino"
          disabled={disabled}
        />
      </div>

      <div className="mb-4">
        <label className="text-[10px] font-mono uppercase tracking-widest text-[#8B97AE] block mb-2">{t("cr.freshProof")}</label>
        <label className={`inline-flex items-center gap-2 px-3 h-10 border border-white/20 transition-colors ${disabled ? "opacity-40 cursor-not-allowed" : "cursor-pointer hover:bg-white/5"}`}>
          <Upload className="w-3 h-3" />
          <span className="text-[11px] font-mono uppercase tracking-widest">{uploading ? t("cr.uploading") : t("cr.addProof")}</span>
          <input data-testid="cr-proof-input-casino" type="file" multiple accept="image/*,application/pdf,video/*" className="sr-only" disabled={disabled}
            onChange={(e) => { if (e.target.files?.length) upload([...e.target.files]); e.target.value = ""; }} />
        </label>
        {proofs.length > 0 && (
          <ul className="mt-3 space-y-1" data-testid="cr-proof-list-casino">
            {proofs.map((p, i) => (
              <li key={i} className="flex items-center gap-2 border border-white/10 px-2 py-1 text-[11px] font-mono">
                <ShieldCheck className="w-3 h-3 text-[#2DD4A7]" />
                <span className="text-[#E8ECF4] truncate flex-1">{p.filename}</span>
                <button onClick={() => removeProof(i)} className="text-[#F26D6D] hover:text-white"><X className="w-3 h-3" /></button>
              </li>
            ))}
          </ul>
        )}
      </div>

      <textarea data-testid="cr-note-casino" rows={2} maxLength={1000} value={note} onChange={(e) => setNote(e.target.value)} disabled={disabled}
        placeholder={t("cr.notePlaceholder")}
        className="w-full rounded-none bg-transparent border border-white/20 text-xs text-[#E8ECF4] p-3 mb-4 resize-none disabled:opacity-40" />

      <button data-testid="cr-submit-casino" onClick={submit} disabled={submitBlocked}
        className="inline-flex items-center gap-2 px-4 h-10 bg-[#F5B841] text-[#04150F] font-mono text-xs uppercase tracking-widest hover:bg-[#2DD4A7] transition-colors disabled:opacity-40">
        <Send className="w-3 h-3" />{busy ? "…" : t("cr.submitCasino")}
      </button>
      {submitBlocked && !busy && (
        <div className="mt-2 text-[10px] font-mono text-[#8B97AE]" data-testid="cr-submit-hint-casino">
          {disabled
            ? t("cr.hint.otherCrOpen")
            : !changed
              ? t("cr.hint.casinoDifferent")
              : !inRange
                ? t("cr.hint.oob")
                : proofs.length === 0
                  ? t("cr.hint.needProof")
                  : ""}
        </div>
      )}
    </section>
  );
}

// -------------- CASE SUB-PANEL --------------
function CaseChangePanel({ me, disabled, pending, pendingVertical, onCancelPending, onSubmitted }) {
  const { t } = useI18n();
  const currentCase = Number(me?.offeredVolumeByVertical?.case_opening || 0);
  const [caseWager, setCaseWager] = useState(Number(me?.caseWagerSelfReported || 0));
  const [offCase, setOffCase] = useState(currentCase);
  const [note, setNote] = useState("");
  const [proofs, setProofs] = useState([]);
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState(false);
  // Iter 208 — Reporting-period selector (30 | 90 days). Independent from
  // the casino selector; sourced from the per-vertical `caseReportPeriodDays`
  // field. Captured on submit; admin cannot override on approve.
  const [caseReportPeriod, setCaseReportPeriod] = useState(() => {
    const p = Number(me?.caseReportPeriodDays);
    return p === 30 || p === 90 ? p : 90;
  });

  const caseWeighted = Number(caseWager || 0);
  const bounds = caseWeighted > 0
    ? { min: Math.round(caseWeighted * OFFERED_MIN_FRACTION), max: Math.round(caseWeighted * OFFERED_MAX_FRACTION) }
    : null;
  const changed = offCase > 0 && offCase !== currentCase;
  const inRange = !changed || (bounds && offCase >= bounds.min && offCase <= bounds.max);
  const submitBlocked = disabled || busy || proofs.length === 0 || !changed || !inRange;

  const upload = useCallback(async (files) => {
    setUploading(true);
    try {
      const uploaded = [];
      for (const f of files) {
        const fd = new FormData();
        fd.append("file", f);
        const { data } = await api.post("/players/me/proofs", fd, { headers: { "Content-Type": "multipart/form-data" } });
        uploaded.push({ id: data.id, path: data.path, filename: data.filename, contentType: data.contentType, size: data.size });
      }
      setProofs((old) => [...old, ...uploaded]);
      toast.success(`Uploaded ${files.length} proof${files.length > 1 ? "s" : ""}`);
    } catch (e) { toast.error(apiErrorMessage(e)); }
    finally { setUploading(false); }
  }, []);
  const removeProof = async (idx) => {
    const p = proofs[idx];
    try { await api.delete(`/players/me/proofs?path=${encodeURIComponent(p.path)}`); } catch (e) { /* soft */ }
    setProofs(proofs.filter((_, i) => i !== idx));
  };

  const submit = async () => {
    if (proofs.length === 0) { toast.error(t("cr.toast.attachProof")); return; }
    if (!changed) { toast.error(t("cr.toast.enterDifferentCase")); return; }
    if (!inRange) { toast.error(t("cr.toast.caseOob")); return; }
    setBusy(true);
    try {
      const body = {
        vertical: "case_opening",
        proposedCaseWager: caseWager,
        proposedOfferedVolumeByVertical: { case_opening: offCase },
        proposedCaseReportPeriodDays: caseReportPeriod,
        proofFileIds: proofs.map((p) => p.id).filter(Boolean),
        note,
      };
      const { data } = await api.post("/players/me/change-request", body);
      toast.success(t("cr.toast.caseSubmitted"));
      onSubmitted && onSubmitted(data);
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error((d && typeof d === "object" && d.message) || apiErrorMessage(e));
    } finally { setBusy(false); }
  };

  return (
    <section className="lg:col-span-12 border border-[#F5B841]/25 bg-[#F5B841]/[0.04] p-6 lg:p-8" data-testid="cr-panel-case">
      <div className="flex items-center gap-2 mb-1">
        <Coins className="w-4 h-4 text-[#F5B841]" />
        <h2 className="font-display font-bold text-lg">{t("cr.case.title")}</h2>
      </div>
      <p className="text-xs text-[#8B97AE] mb-4 max-w-3xl">
        {t("cr.case.desc")}
      </p>

      {pending && (
        <PendingBanner
          pendingVertical={pendingVertical}
          submittedAt={pending?.submittedAt}
          onCancel={onCancelPending}
          testIdSuffix="case"
        />
      )}

      <div className="mb-4" data-testid="cr-case-wager-wrap">
        <label className="text-[10px] font-mono uppercase tracking-widest text-[#8B97AE] block mb-1">
          {t("cr.case.wagerBasis")} <span className="text-[9px] text-[#8B97AE]/60 ml-1">× 1.0</span>
        </label>
        <input
          data-testid="cr-case-wager"
          type="text" inputMode="numeric" disabled={disabled}
          value={caseWager ? Number(caseWager).toLocaleString() : ""}
          onChange={(e) => setCaseWager(Number(e.target.value.replace(/[^\d]/g, "")) || 0)}
          className="w-full md:w-1/2 rounded-none bg-transparent border border-white/20 h-10 font-mono tabular-nums text-right text-[#E8ECF4] px-3 disabled:opacity-40"
        />
        <div className="text-[10px] font-mono uppercase tracking-widest text-[#8B97AE] mt-1">
          {t("cr.case.liveWeighted")} <span className="text-[#E8ECF4] tabular-nums" data-testid="cr-case-weighted-live">${caseWeighted.toLocaleString()}</span>
        </div>
      </div>

      {/* Iter 208 — Reporting-period selector (30 | 90). Independent from
          casino. Captured on submit; admin cannot override on approve. */}
      <div className="mb-4" data-testid="cr-report-toggles-case">
        <div className="text-[10px] font-mono uppercase tracking-widest text-[#8B97AE] mb-1">{t("onboarding.reportPeriodLabel")}</div>
        <div className="inline-flex border border-white/20" role="tablist">
          {[30, 90].map((d) => (
            <button
              key={d}
              type="button"
              data-testid={`cr-report-period-case-${d}`}
              disabled={disabled}
              onClick={() => setCaseReportPeriod(d)}
              className={`px-3 h-9 text-[11px] font-mono uppercase tracking-widest transition-colors disabled:opacity-40 disabled:cursor-not-allowed ${
                Number(caseReportPeriod) === d
                  ? "bg-[#F5B841] text-[#04150F]"
                  : "text-[#8B97AE] hover:text-[#E8ECF4]"
              }`}
            >{d === 30 ? t("card.wager.period30") : t("card.wager.period90")}</button>
          ))}
        </div>
      </div>

      <div className="mb-4">
        <OfferedRow
          vertical="case_opening"
          label={t("cr.label.case")}
          currentLocked={currentCase}
          val={offCase}
          setVal={setOffCase}
          weighted={caseWeighted}
          testId="cr-offered-case"
          disabled={disabled}
        />
      </div>

      <div className="mb-4">
        <label className="text-[10px] font-mono uppercase tracking-widest text-[#8B97AE] block mb-2">{t("cr.freshProof")}</label>
        <label className={`inline-flex items-center gap-2 px-3 h-10 border border-white/20 transition-colors ${disabled ? "opacity-40 cursor-not-allowed" : "cursor-pointer hover:bg-white/5"}`}>
          <Upload className="w-3 h-3" />
          <span className="text-[11px] font-mono uppercase tracking-widest">{uploading ? t("cr.uploading") : t("cr.addProof")}</span>
          <input data-testid="cr-proof-input-case" type="file" multiple accept="image/*,application/pdf,video/*" className="sr-only" disabled={disabled}
            onChange={(e) => { if (e.target.files?.length) upload([...e.target.files]); e.target.value = ""; }} />
        </label>
        {proofs.length > 0 && (
          <ul className="mt-3 space-y-1" data-testid="cr-proof-list-case">
            {proofs.map((p, i) => (
              <li key={i} className="flex items-center gap-2 border border-white/10 px-2 py-1 text-[11px] font-mono">
                <ShieldCheck className="w-3 h-3 text-[#2DD4A7]" />
                <span className="text-[#E8ECF4] truncate flex-1">{p.filename}</span>
                <button onClick={() => removeProof(i)} className="text-[#F26D6D] hover:text-white"><X className="w-3 h-3" /></button>
              </li>
            ))}
          </ul>
        )}
      </div>

      <textarea data-testid="cr-note-case" rows={2} maxLength={1000} value={note} onChange={(e) => setNote(e.target.value)} disabled={disabled}
        placeholder={t("cr.notePlaceholder")}
        className="w-full rounded-none bg-transparent border border-white/20 text-xs text-[#E8ECF4] p-3 mb-4 resize-none disabled:opacity-40" />

      <button data-testid="cr-submit-case" onClick={submit} disabled={submitBlocked}
        className="inline-flex items-center gap-2 px-4 h-10 bg-[#F5B841] text-[#04150F] font-mono text-xs uppercase tracking-widest hover:bg-[#2DD4A7] transition-colors disabled:opacity-40">
        <Send className="w-3 h-3" />{busy ? "…" : t("cr.submitCase")}
      </button>
      {submitBlocked && !busy && (
        <div className="mt-2 text-[10px] font-mono text-[#8B97AE]" data-testid="cr-submit-hint-case">
          {disabled
            ? t("cr.hint.otherCrOpen")
            : !changed
              ? t("cr.hint.caseDifferent")
              : !inRange
                ? t("cr.hint.oob")
                : proofs.length === 0
                  ? t("cr.hint.needProof")
                  : ""}
        </div>
      )}
    </section>
  );
}

export default function ChangeRequestPanel({ me, lock, onSubmitted }) {
  const { t } = useI18n();
  // Iter 149 — Per-vertical disabled state derived from the per-vertical
  // maps returned by GET /players/me/edit-lock. Each panel decides
  // independently whether it accepts a new submission. A live casino
  // contract locks ONLY the casino panel; the case panel stays open
  // (and vice versa) — matching the backend 5-gate scope fix.
  const activeByV = lock?.hasActiveContractByVertical || {};
  const pendingByV = lock?.pendingChangeRequestByVertical || {};

  // Back-compat fallback: if the server response predates Iter 149,
  // derive per-vertical pending from the legacy scalars.
  const legacyPending = lock?.pendingChangeRequest;
  const legacyPendingV = lock?.pendingVertical;
  const pendingCasino = pendingByV.casino ?? (legacyPendingV === "casino" ? legacyPending : null);
  const pendingCase = pendingByV.case_opening ?? (legacyPendingV === "case_opening" ? legacyPending : null);

  // Each panel is disabled if EITHER (a) this vertical has a live
  // contract, OR (b) this vertical has a pending CR under review.
  // A live contract or pending CR on the OTHER vertical does NOT
  // disable this one anymore.
  const casinoLive = !!activeByV.casino;
  const caseLive = !!activeByV.case_opening;
  const casinoDisabled = casinoLive || !!pendingCasino;
  const caseDisabled = caseLive || !!pendingCase;

  const onCancelPendingFor = async (pending) => {
    if (!pending) return;
    if (!window.confirm(t("cr.confirmCancel"))) return;
    try {
      await api.delete(`/players/me/change-request/${pending.id}`);
      toast.success(t("cr.toast.cancelled"));
      onSubmitted && onSubmitted(null);
    } catch (e) { toast.error(apiErrorMessage(e)); }
  };

  return (
    <>
      {casinoLive && (
        <div className="border border-[#F5B841]/40 bg-[#F5B841]/[0.06] p-3 flex items-start gap-2" data-testid="cr-live-contract-lock-casino">
          <AlertCircle className="w-3.5 h-3.5 mt-0.5 shrink-0 text-[#F5B841]" />
          <span className="text-[11px] font-mono text-[#F5B841]">
            {t("cr.liveLock.casino")}
          </span>
        </div>
      )}
      <CasinoChangePanel
        me={me}
        disabled={casinoDisabled}
        pending={pendingCasino}
        pendingVertical={pendingCasino ? "casino" : null}
        onCancelPending={() => onCancelPendingFor(pendingCasino)}
        onSubmitted={onSubmitted}
      />
      {caseLive && (
        <div className="border border-[#F5B841]/40 bg-[#F5B841]/[0.06] p-3 flex items-start gap-2" data-testid="cr-live-contract-lock-case">
          <AlertCircle className="w-3.5 h-3.5 mt-0.5 shrink-0 text-[#F5B841]" />
          <span className="text-[11px] font-mono text-[#F5B841]">
            {t("cr.liveLock.case")}
          </span>
        </div>
      )}
      <CaseChangePanel
        me={me}
        disabled={caseDisabled}
        pending={pendingCase}
        pendingVertical={pendingCase ? "case_opening" : null}
        onCancelPending={() => onCancelPendingFor(pendingCase)}
        onSubmitted={onSubmitted}
      />
    </>
  );
}

export function LockedNotice({ hasActiveContract }) {
  const { t } = useI18n();
  return (
    <section className="lg:col-span-12 border border-white/15 p-6 lg:p-8" data-testid="basis-locked-notice">
      <div className="flex items-center gap-2 mb-2">
        <Lock className="w-4 h-4 text-[#8B97AE]" />
        <h2 className="font-display font-bold text-lg">{t("cr.locked.title")}</h2>
      </div>
      <div className="flex items-start gap-2 text-xs text-[#F5B841]">
        <AlertCircle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
        <span>
          {hasActiveContract ? t("cr.locked.bodyActive") : t("cr.locked.bodyVerified")}
        </span>
      </div>
    </section>
  );
}

// Iter 141 — Read-only "current offered volume per vertical" display block.
// Sourced verbatim from `player.offeredVolumeByVertical.<vertical>`.
export function OfferedVolumeLocked({ me }) {
  const { t } = useI18n();
  const rows = [
    { vertical: "casino",       label: t("cr.label.casino"), value: me?.offeredVolumeByVertical?.casino },
    { vertical: "case_opening", label: t("cr.label.case"),   value: me?.offeredVolumeByVertical?.case_opening },
  ];
  return (
    <section className="lg:col-span-12 border border-[#F5B841]/40 bg-[#F5B841]/[0.05] p-5 lg:p-6 space-y-2" data-testid="offered-volume-locked">
      <div className="text-[10px] font-mono uppercase tracking-widest text-[#F5B841]">
        {t("cr.offeredLocked.title")}
      </div>
      {rows.map(({ vertical, label, value }) => {
        const n = Number(value || 0);
        return (
          <div key={vertical} className="flex flex-wrap items-baseline gap-x-3 gap-y-1 border-t border-[#F5B841]/20 pt-2" data-testid={`offered-locked-row-${vertical}`}>
            <span className="text-[11px] font-mono uppercase tracking-widest text-[#F5B841] min-w-[56px]">{label}</span>
            <span className="text-[10px] font-mono text-[#8B97AE]">{t("cr.offeredLocked.offered")}</span>
            {n > 0 ? (
              <span data-testid={`offered-locked-amount-${vertical}`} className="tabular-nums text-[#E8ECF4] font-mono"
                style={{ fontFamily: "'JetBrains Mono', ui-monospace, monospace" }}>${n.toLocaleString()}</span>
            ) : (
              <span data-testid={`offered-locked-amount-${vertical}`} className="text-[11px] font-mono text-[#8B97AE]">{t("cr.offeredLocked.notSet")}</span>
            )}
            <span className="text-[10px] font-mono uppercase tracking-widest text-[#8B97AE]">{t("cr.offeredLocked.suffix")}</span>
          </div>
        );
      })}
    </section>
  );
}
